import { format, parseISO } from 'date-fns';

// ─── Date Formatting ──────────────────────────────────────────────────────────

/**
 * Parse a date string/Date that may or may not be strict ISO-8601.
 * Tries date-fns' parseISO first (handles ISO strings precisely), then
 * falls back to the native Date constructor for other formats.
 */
const parseFlexibleDate = (date: string | Date): Date => {
  if (date instanceof Date) return date;
  const isoParsed = parseISO(date);
  return isNaN(isoParsed.getTime()) ? new Date(date) : isoParsed;
};

/**
 * Format a date string/Date to a human-readable format
 * @param date - ISO date string, other parseable date string, or Date object
 * @param formatString - date-fns format string (default: 'MMM dd, yyyy')
 * @param fallback - value returned when date is missing/invalid (default: 'N/A')
 * @returns Formatted date string
 */
export const formatDate = (
  date: string | Date | null | undefined,
  formatString: string = 'MMM dd, yyyy',
  fallback: string = 'N/A'
): string => {
  if (!date) return fallback;
  try {
    const parsed = parseFlexibleDate(date);
    if (isNaN(parsed.getTime())) return fallback;
    return format(parsed, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return fallback;
  }
};

/**
 * Format a date string/Date including the time of day.
 * @param date - ISO date string, other parseable date string, or Date object
 * @param formatString - date-fns format string (default: 'MMM d, yyyy h:mm a')
 * @param fallback - value returned when date is missing/invalid (default: 'N/A')
 * @returns Formatted date-time string
 */
export const formatDateTime = (
  date: string | Date | null | undefined,
  formatString: string = 'MMM d, yyyy h:mm a',
  fallback: string = 'N/A'
): string => {
  return formatDate(date, formatString, fallback);
};

/**
 * Format a from/to date pair for filter chips and summaries, e.g.
 * "01/02/2026 - 03/02/2026", "From: 01/02/2026", "To: 03/02/2026".
 * @param from - Start date (ISO string)
 * @param to - End date (ISO string)
 * @returns Human-readable date range, or '' if neither date is provided
 */
export const formatDateRangeDisplay = (from?: string, to?: string): string => {
  if (!from && !to) return '';

  const formatDateStr = (dateStr: string) => formatDate(dateStr, 'dd/MM/yyyy', dateStr);

  if (from && to) {
    return `${formatDateStr(from)} - ${formatDateStr(to)}`;
  } else if (from) {
    return `From: ${formatDateStr(from)}`;
  } else if (to) {
    return `To: ${formatDateStr(to)}`;
  }
  return '';
};

/**
 * Format a currency value
 * @param value - Number to format
 * @param currency - Currency code (default: 'USD')
 * @param locale - Locale (default: 'en-US')
 * @returns Formatted currency string
 */
export const formatCurrency = (
  value: number | null | undefined,
  currency: string = 'USD',
  locale: string = 'en-US'
): string => {
  if (value === null || value === undefined) return 'N/A';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(value);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${value}`;
  }
};

/**
 * Format a number with commas
 * @param value - Number to format
 * @param locale - Locale (default: 'en-US')
 * @returns Formatted number string
 */
export const formatNumber = (
  value: number | null | undefined,
  locale: string = 'en-US'
): string => {
  if (value === null || value === undefined) return 'N/A';
  try {
    return new Intl.NumberFormat(locale).format(value);
  } catch (error) {
    console.error('Error formatting number:', error);
    return `${value}`;
  }
};

/**
 * Format a phone number
 * @param phoneNumber - Phone number to format
 * @returns Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return 'N/A';
  
  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Format based on length
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  
  // Return original if can't format
  return phoneNumber;
};

/**
 * Truncate text with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length (default: 50)
 * @returns Truncated text
 */
export const truncateText = (
  text: string | null | undefined,
  maxLength: number = 50
): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

/**
 * Format file size
 * @param bytes - Size in bytes
 * @param decimals - Decimal places (default: 2)
 * @returns Formatted file size
 */
export const formatFileSize = (
  bytes: number | null | undefined,
  decimals: number = 2
): string => {
  if (bytes === null || bytes === undefined) return 'N/A';
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};

// ─── String / Field Formatting ────────────────────────────────────────────────

/**
 * Get a file's extension (without the leading dot), lowercased.
 * @param fileName - File name or path
 * @returns Lowercased extension, or '' if none is present
 */
export const getFileExtension = (fileName: string | null | undefined): string => {
  if (!fileName) return '';
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1 || lastDot === fileName.length - 1) return '';
  return fileName.slice(lastDot + 1).toLowerCase();
};

/**
 * Format a snake_case field name for display, e.g. 'first_name' -> 'First Name'
 * @param field - snake_case field name
 * @returns Title-cased, space-separated field name
 */
export function formatFieldName(field: string): string {
  return field
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}